import isEmpty from 'lodash/isEmpty';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { TFunction } from '@lyearn/i18n';
import { PersonEditOutline, PhoneOutline } from '@lyearn/icons';
import { css } from '@lyearn/style';
import { Fields, FieldType } from '@/components/Form';
import { MultiStepSection } from '@/components/MultiStepForm/types';
import AddCover from '@/modules/events/views/EventEditor/components/AddCover';
import { CreateMediaInput } from '@/types/schema';

import { CreateClientContactFormValueType } from './types';

const COVER_IMAGE_STYLES = css`
  width: 12.4rem;
  height: 12.4rem;
  border-radius: 9999rem;
`;
const COVER_IMAGE_SIZE = { width: 124, height: 124 };

export const CREATE_CLIENT_INITIAL_VALUES: CreateClientContactFormValueType = {
  name: '',
  email: undefined,
  position: undefined,
  phone: undefined,
  mobile: undefined,
  additionalInfo: undefined,
  icon: undefined,
};

const EMPTY_ARRAY: never[] = [];

export const getCreateClientContactFormInitialValues = (): CreateClientContactFormValueType => {
  return CREATE_CLIENT_INITIAL_VALUES;
};

export const validateCreateClientContactForm =
  (t: TFunction) => (values: CreateClientContactFormValueType) => {
    const errors: any = {};

    if (isEmpty(values.name.trim())) {
      errors.name = t('Clients:ContactForm.ContactNameRequired');
    }

    if (isEmpty(values.email?.trim())) {
      errors.email = t('Clients:ContactForm.ContactEmailRequired');
    } else if (!isEmail(values.email!)) {
      errors.email = t('Clients:ContactForm.ContactEmailInvalid');
    }

    if (isEmpty(values.position?.trim())) {
      errors.position = t('Clients:ContactForm.PositionRequired');
    }

    if (isEmpty(values.phone?.trim())) {
      errors.phone = t('Clients:ContactForm.PhoneRequired');
    } else if (!isMobilePhone(values.phone!, 'any')) {
      errors.phone = t('Clients:ContactForm.PhoneInvalid');
    }

    if (!isEmpty(values.mobile) && !isMobilePhone(values.mobile!, 'any')) {
      errors.mobile = t('Clients:ContactForm.MobileInvalid');
    }

    return errors;
  };

export const getCreateClientFormFields = ({
  t,
  defaultCover,
  isEditing,
}: {
  t: TFunction;
  defaultCover: CreateMediaInput;
  isEditing?: boolean;
}): Fields<CreateClientContactFormValueType> => ({
  name: {
    name: 'name',
    type: FieldType.Text,
    label: t('Clients:ContactForm.ContactName'),
    placeholder: t('Clients:ContactForm.ContactNamePlaceholder'),
    required: true,
    props: {
      characterLimit: 50,
    },
  },
  email: {
    name: 'email',
    type: FieldType.Email,
    label: t('Clients:ContactForm.ContactEmail'),
    placeholder: t('Clients:ContactForm.ContactEmailPlaceholder'),
    required: true,
  },
  position: {
    name: 'position',
    type: FieldType.Text,
    label: t('Clients:ContactForm.Position'),
    placeholder: t('Clients:ContactForm.PositionPlaceholder'),
    required: true,
    props: {
      characterLimit: 64,
    },
  },
  phone: {
    name: 'phone',
    type: FieldType.Text,
    label: t('Clients:ContactForm.Phone'),
    placeholder: t('Clients:ContactForm.PhonePlaceholder'),
    required: true,
  },
  mobile: {
    name: 'mobile',
    type: FieldType.Text,
    label: t('Clients:ContactForm.Mobile'),
    placeholder: t('Clients:ContactForm.MobilePlaceholder'),
  },
  icon: {
    name: 'icon',
    type: FieldType.SlowCustom,
    Component: ({ value, onChange }) => {
      return (
        <AddCover
          cover={value ?? undefined}
          coverImageStyles={COVER_IMAGE_STYLES}
          defaultCover={defaultCover}
          description={t('Clients:ContactForm.ContactImageDescription')}
          disabled={false}
          imageSize={COVER_IMAGE_SIZE}
          overrideError={true}
          title={t('Clients:ContactForm.ContactImage')}
          onChange={onChange}
        />
      );
    },
  },
  additionalInfo: {
    name: 'additionalInfo',
    type: FieldType.Multiline,
    label: t('Clients:ContactForm.Notes'),
    placeholder: t('Clients:ContactForm.NotesPlaceholder'),
    props: {
      characterLimit: 250,
    },
  },
});

const SECTION_CLASSES: MultiStepSection['classes'] = { panel: 'w-full px-32 py-24' };

export const getSectionLayout = ({ t }: { t: TFunction }): MultiStepSection[] => [
  {
    id: 'BasicInformation',
    name: t('Clients:ContactForm.BasicInformation'),
    Icon: PersonEditOutline,
    classes: SECTION_CLASSES,
    sectionLayout: [
      {
        id: 'BasicInformation',
        name: t('Clients:ContactForm.BasicInformation'),
        description: t('Clients:ContactForm.BasicInformationDescription'),
        layout: ['name', 'email', 'position', 'icon'],
      },
    ],
  },
  {
    id: 'ContactDetails',
    name: t('Clients:ContactForm.ContactDetails'),
    Icon: PhoneOutline,
    classes: SECTION_CLASSES,
    sectionLayout: [
      {
        id: 'ContactDetails',
        name: t('Clients:ContactForm.ContactDetails'),
        description: t('Clients:ContactForm.ContactDetailsDescription'),
        layout: [['phone', 'mobile'], 'additionalInfo'],
      },
    ],
  },
];
